import { SORT_KEYS, type SortKey } from '@arrow/game-engine';

/**
 * Play's state in the address bar, and the context a game opened from Play carries, so
 * Back, a reload and a shared link keep it (docs/WEB-PLATFORM.md, "Play: the archive"):
 *
 *   /play?stack=missed&sort=old         an open stack, re-sorted
 *   /play?q=hard+october                a search
 *   /daily/17?l=hard&from=stack&stack=missed&sort=old
 *                                       a game opened from that stack: "‹ Play" goes back to
 *                                       it, and the poster's way on is the next in it
 */

/** What's open on Play. All null: the stacks. */
export interface PlayParams {
	stack: string | null;
	q: string | null;
	sort: SortKey | null;
}

/** How a game was reached from Play (`daily_opened.via`). */
export type PlayVia = 'stack' | 'search' | 'lookup' | 'next';
const VIAS: readonly PlayVia[] = ['stack', 'search', 'lookup', 'next'];

export interface PlayContext extends PlayParams {
	from: PlayVia;
}

const STACK_ID = /^[a-z][a-z0-9-]{0,31}$/;
const MAX_Q = 120;

function params(search: string): URLSearchParams {
	try {
		return new URLSearchParams(search);
	} catch {
		return new URLSearchParams();
	}
}

/** Reads `stack`, `q` and `sort` (anything odd is dropped, never thrown on). */
export function readPlayParams(search: string): PlayParams {
	const p = params(search);
	const stack = p.get('stack');
	const q = p.get('q')?.slice(0, MAX_Q).trim() || null;
	const sort = p.get('sort');
	return {
		stack: stack && STACK_ID.test(stack) ? stack : null,
		q,
		sort: (SORT_KEYS as readonly string[]).includes(sort ?? '') ? (sort as SortKey) : null,
	};
}

/** A game's Play context, from its query (`from` plus the Play params), or null. */
export function readPlayContext(search: string): PlayContext | null {
	const from = params(search).get('from');
	if (!from || !(VIAS as readonly string[]).includes(from)) return null;
	return { from: from as PlayVia, ...readPlayParams(search) };
}

function query(entries: Array<[string, string | null | undefined]>): string {
	const p = new URLSearchParams();
	for (const [k, v] of entries) if (v) p.set(k, v);
	const s = p.toString();
	return s ? `?${s}` : '';
}

/** The Play page for these params. */
export function playPath(p: Partial<PlayParams> = {}): string {
	return `/play${query([
		['stack', p.stack],
		['q', p.q],
		['sort', p.sort],
	])}`;
}

/** The query a game link carries for its context (after `l`). */
export function contextEntries(ctx: PlayContext | null | undefined): Array<[string, string | null]> {
	if (!ctx) return [];
	return [
		['from', ctx.from],
		['stack', ctx.stack],
		['q', ctx.q],
		['sort', ctx.sort],
	];
}

/** `/daily/17?l=hard&from=stack&stack=missed`. */
export function dailyPath(number: number, level: string | null, ctx?: PlayContext | null): string {
	return `/daily/${number}${query([['l', level], ...contextEntries(ctx)])}`;
}

/** `/play/<id>?from=stack&stack=practice`. */
export function practicePath(id: string, level: string | null, ctx?: PlayContext | null): string {
	return `/play/${encodeURIComponent(id)}${query([['l', level], ...contextEntries(ctx)])}`;
}

/** Where "‹ Play" goes from a game with this context: the same stack or search. */
export function backToPlay(ctx: PlayContext | null | undefined): string {
	return ctx ? playPath(ctx) : '/play';
}
